// @ts-nocheck
// Call Stats — Recent Call Log table: columns + DataTable, split out of the
// main panel. Direction/status badges restyled as flat Tailwind pills on
// the app's own semantic tokens instead of the old `agent-call-stats-
// direction`/`agent-call-stats-status` cti.css classes.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { FiPhoneIncoming, FiPhoneOutgoing } from 'react-icons/fi';
import { Eye, Phone, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/data-table/DataTable';
import { downloadCsv, stampedFileName } from '@/components/data-table/csv-export';
import {
  CallTranscriptButton,
  loadTranscriptAvailability,
  useCanViewTranscripts,
  useTranscriptAvailability,
} from '@/features/transcripts/CallTranscript';
import MediaAudioList, { mediaUrl } from '@/components/partials/components/MediaAudioList';
import { canReadRecordings, isAgentVoicemailEnabled } from '@/utilities/recordingsAcl';
import CopyButton from '@/features/api-webhook/components/CopyButton';
import CallQualityDetail from '@/features/ivr-reports/components/CallQualityDetail';
import CdrCallDetailDialog from '@/features/ivr-reports/components/CdrCallDetailDialog';
import { getUser } from '@/utilities/setting';
import { toast } from 'sonner';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { API_DISPOSITION_BY_CALL, API_DISPOSITIONS_BY_CALLS, API_HANGUP_CAUSES } from '@/service/url';
import { formatDuration } from './format';
import { agentCallStatus, callDurationSeconds, callOutcomeEvt } from '../../ctiHelpers';

function Pill({ tone, children }: any) {
  const toneClass =
    tone === 'primary'
      ? 'bg-primary/10 text-primary'
      : tone === 'good'
        ? 'bg-success/10 text-success'
        : tone === 'warn'
          ? 'bg-warning/10 text-warning'
          : tone === 'crit'
            ? 'bg-destructive/10 text-destructive'
            : 'bg-muted text-muted-foreground';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${toneClass}`}>
      {children}
    </span>
  );
}

// Recording / Voicemail as main columns (same cell as the follow-ups panel).
const mediaCol = (kind, header) => ({
  id: kind,
  accessorFn: (call) => ((call?.media || []).some((item) => item?.kind === kind) ? header : ''),
  header,
  enableSorting: true,
  cell: ({ row }) => (
    <MediaAudioList media={(row.original?.media || []).filter((item) => item?.kind === kind)} compact />
  ),
});

const CALL_TYPE_FLAGS = [
  ['is_transfer', 'Transfer'],
  ['is_conference', 'Conference'],
  ['is_ivr', 'IVR'],
  ['is_agent_lead', 'Agent Lead'],
  ['is_whisper', 'Whisper'],
  ['is_monitor', 'Monitor'],
  ['is_barge_in', 'Barge In'],
  ['is_follow_up', 'Follow-up'],
  ['is_voicemail', 'Voicemail'],
];

function callTypeTags(call) {
  return [
    isIncomingCall(call) ? 'Inbound' : 'Outbound',
    ...CALL_TYPE_FLAGS.filter(([flag]) => call?.[flag]).map(([, label]) => label),
  ];
}

const isIncomingCall = (call) => {
  const dir = String(call?.direction || '').toLowerCase();
  return dir === 'incoming' || dir === 'inbound' || dir === 'in';
};

const CALL_STATUS = {
  connected: ['Connected', 'good'],
  failed: ['Failed', 'crit'],
  abandoned: ['Abandoned', 'warn'],
};

const agentOutcome = (call) => (call?.is_missed ? 'Missed' : call?.is_declined ? 'Declined' : '');

function callStatusLabel(call) {
  const known = CALL_STATUS[agentCallStatus(call)];
  if (known) return known[0];
  if (call?.answered) return 'Connected';
  return agentOutcome(call) || 'Unanswered';
}

const StatusPill = ({ label }) => (
  <span className={`call-evt call-evt--static call-evt--${callOutcomeEvt(label)}`}>{label}</span>
);

// Call duration = stored total_duration; older rows keep the billing rule (0 when the other party never connected).
const talkSeconds = (call) =>
  call.total_duration != null && call.total_duration !== ''
    ? callDurationSeconds(call)
    : call.other_party_call_status && call.other_party_call_status !== 'connected'
      ? 0
      : Number(call.billing_duration) || 0;

const capitalize = (v) => {
  const s = String(v || '');
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
};

const durationCol = (id, accessorKey, header) => ({
  id,
  accessorKey,
  header,
  // formatDuration turns missing / invalid values into 0s.
  cell: ({ getValue }) => formatDuration(getValue()),
  enableSorting: true,
});

const idCol = (id, accessorKey, header) => ({
  id,
  accessorKey,
  header,
  cell: ({ getValue }) => {
    const value = getValue();
    if (!value) return '-';
    return (
      <span className="inline-flex items-center gap-1">
        <span className="block max-w-[180px] truncate font-mono text-[11px]" title={String(value)}>
          {value}
        </span>
        <CopyButton text={String(value)} className="h-6 w-6 shrink-0 p-0" />
      </span>
    );
  },
  enableSorting: true,
});

const SEARCHABLE_COLUMN_IDS = new Set(['call_sid', 'trace_id', 'did', 'agent_name']);

const textCol = (id, accessorKey, header, fallback = '-') => ({
  id,
  accessorKey,
  header,
  cell: ({ getValue }) => getValue() || fallback,
  enableSorting: true,
});

const secs = (v) => formatDuration(Number(v) > 0 ? Number(v) : 0);
const at = (v) => (v ? moment(v).format('DD-MM-YYYY HH:mm:ss') : '-');
const fmtFieldLabel = (key) => String(key).replace(/[_-]+/g, ' ').trim();
const fmtFieldValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.map(fmtFieldValue).join(', ') : '-';
  if (typeof value === 'object') {
    try { return JSON.stringify(value); } catch { return '-'; }
  }
  return String(value);
};

function legStatus(status) {
  const known = CALL_STATUS[status];
  return known ? <StatusPill label={known[0]} /> : '-';
}

function DetailCard({ title, items, children }: any) {
  return (
    <section className="rounded-xl border border-border/60 bg-card p-3">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</div>
      {items ? (
        <dl className="m-0 grid gap-x-4 gap-y-1 text-xs [grid-template-columns:minmax(110px,auto)_1fr]">
          {items.map(([label, value]) => (
            <React.Fragment key={label}>
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="m-0 min-w-0 break-words font-medium text-foreground">
                {value === null || value === undefined || value === '' ? '-' : value}
              </dd>
            </React.Fragment>
          ))}
        </dl>
      ) : null}
      {children}
    </section>
  );
}

function DispositionDetail({ call }: any) {
  const [state, setState] = useState<{ status: string; data: any }>({ status: 'idle', data: null });
  const load = () => {
    setState({ status: 'loading', data: null });
    const q = new URLSearchParams();
    if (call.call_sid) q.set('call_sid', call.call_sid);
    if (call.trace_id) q.set('trace_id', call.trace_id);
    getApiCall(`${API_DISPOSITION_BY_CALL}?${q}`)
      .then((data) => setState({ status: 'done', data: data || null }))
      .catch(() => setState({ status: 'error', data: null }));
  };
  const d = state.data;
  const fields = Object.entries(d?.data && typeof d.data === 'object' ? d.data : {});
  const hideBtn = (
    <button
      type="button"
      onClick={() => setState({ status: 'idle', data: null })}
      className="ml-auto rounded-md border border-border bg-background px-2.5 py-1 font-medium text-foreground hover:bg-muted"
    >
      Hide
    </button>
  );

  return (
    <DetailCard title="Disposition">
      {state.status === 'idle' || state.status === 'error' ? (
        <div className="flex items-center gap-2 text-xs">
          <button
            type="button"
            onClick={load}
            className="rounded-md border border-border bg-background px-2.5 py-1 font-medium text-foreground hover:bg-muted"
          >
            {state.status === 'error' ? 'Retry' : 'View saved disposition'}
          </button>
          {state.status === 'error' ? <span className="text-destructive">Could not load disposition</span> : null}
        </div>
      ) : state.status === 'loading' ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : !d ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">No disposition saved{hideBtn}</div>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            <span>Form: <span className="font-medium text-foreground">{d.disposition_form_name || '-'}</span></span>
            <span>Submitted: <span className="font-medium text-foreground">{at(d.created_at)}</span></span>
            {d.edited_at ? <span>Edited: <span className="font-medium text-foreground">{at(d.edited_at)}</span></span> : null}
            {d.is_auto_submit ? <span className="font-medium text-warning">Auto-submitted</span> : null}
            {hideBtn}
          </div>
          {fields.length ? (
            <dl className="m-0 grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
              {fields.map(([key, value]) => (
                <div key={key} className="min-w-0">
                  <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">{fmtFieldLabel(key)}</dt>
                  <dd className="m-0 break-words text-xs text-foreground">{fmtFieldValue(value)}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <div className="text-xs text-muted-foreground">No fields saved for this disposition.</div>
          )}
        </div>
      )}
    </DetailCard>
  );
}

function CallDetailRow({ call, onView }: any) {
  const incoming = isIncomingCall(call);
  const otherParty = incoming ? 'Caller' : 'Callee';
  const outcome = agentOutcome(call);
  const qualityAltIds = useMemo(() => [call.trace_id], [call.trace_id]);
  return (
    // sticky: stays in view when the wide table is scrolled sideways
    <div className="sticky left-0 max-w-7xl space-y-3 p-4">
      {call?.trace_id ? (
        <Button type="button" variant="outline" size="sm" className="h-7 gap-1 px-2" onClick={() => onView(call)}>
          <Eye size={14} /> View call details
        </Button>
      ) : null}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        <DetailCard
          title="Call Legs"
          items={[
            [
              'Agent call status',
              <span className="inline-flex items-center gap-1">
                {legStatus(call.call_status)}
                {outcome ? <span className="text-[10px] text-muted-foreground">{outcome}</span> : null}
              </span>,
            ],
            ['Agent ring', secs(call.bridge_ring_duration)],
            [`${otherParty} call status`, legStatus(call.other_party_call_status)],
            [`${otherParty} ring`, secs(call.other_party_ring_duration)],
            ['Hangup cause', capitalize(call.hangup_cause)],
          ]}
        />
        <DetailCard
          title="Timing"
          items={[
            // ['Start', at(call.start_time)],
            // ['Answered', at(call.bridge_time)],
            // ['End', at(call.end_time)],
            ['Call duration', secs(talkSeconds(call))],
            ['Talk time', secs(call.patch_duration)],
            ['Hold', secs(call.hold_duration)],
            ['Mute', secs(call.mute_duration)],
            ['Wrap-up', secs(call.wrap_up_seconds)],
          ]}
        />
        <DetailCard
          title="Customer & Context"
          items={[
            ['Customer number', call.number],
            ['Contact', call.caller_profile],
            ['Circle', call.circle_name],
            ['Operator', call.operator_name],
            ['DID', call.did],
            ['Campaign', call.campaign_name || 'No campaign'],
            ['Call group', call.call_group_name || 'No call group'],
          ]}
        />
      </div>
      <div className="flex flex-col gap-3 md:flex-row">
        <div className="min-w-0 flex-1">
          <DispositionDetail call={call} />
        </div>
        <div className="min-w-0 flex-1 empty:hidden [&>.call-quality-detail]:!m-0 [&>.call-quality-detail]:bg-card">
          <CallQualityDetail callSid={call.call_sid} altIds={qualityAltIds} />
        </div>
      </div>
    </div>
  );
}

// Moved into the expanded row; still toggleable from Columns.
const HIDDEN_BY_DEFAULT = {
  caller_profile: false,
  campaign_name: false,
  call_group_name: false,
  hold_duration: false,
  mute_duration: false,
  wrap_up_seconds: false,
};

// showAgent: team-lead view (rows from several agents) — adds an Agent column + export field.
export default function CallLogTable({
  rows: rawRows,
  loading,
  showAgent = false,
  tableId = 'cti.agent-call-stats.recent-calls.v2',
  exportName = 'calls', // file name prefix; a timestamp is added per export
  onCallNumber = null, // agent's own log: call icon next to the number (backend re-checks before dialing)
  callBlockedReason = null,
  // Server-paged mode: rows are one page. serverPaging = { pageIndex, pageSize, rowCount, onChange };
  // onSearchChange gets the debounced search text (searched server-side, back to page 1);
  // fetchExportRows() resolves { rows, total, fetched } for the whole filtered log.
  serverPaging = null,
  onSearchChange = null,
  fetchExportRows = null,
  exportLabel = 'Export',
}: any) {
  const [searchText, setSearchText] = useState('');
  useEffect(() => {
    if (!onSearchChange) return undefined;
    const t = setTimeout(() => onSearchChange(searchText.trim()), 300);
    return () => clearTimeout(t);
  }, [searchText, onSearchChange]);
  const exportingRef = useRef(false);

  // Agent's own logs: no Missed / Declined badges or text (calls.is_missed / is_declined).
  // The team-lead view keeps them.
  const rows = useMemo(
    () => (showAgent || !rawRows ? rawRows : rawRows.map((r) => ({ ...r, is_missed: false, is_declined: false }))),
    [rawRows, showAgent]
  );
  const canViewRecordings = canReadRecordings();
  const canViewVoicemails = useMemo(() => isAgentVoicemailEnabled(), []);
  const canViewTranscripts = useCanViewTranscripts();

  const transcriptLineCount = useTranscriptAvailability(
    useMemo(() => (rows || []).map((row) => row?.trace_id), [rows])
  );

  const [detailCall, setDetailCall] = useState<any>(null);
  const [hangupCauseMap, setHangupCauseMap] = useState<Record<string, string> | null>(null);
  useEffect(() => {
    if (!detailCall || hangupCauseMap) return;
    getApiCall(API_HANGUP_CAUSES)
      .then((resp: any) => {
        const list = Array.isArray(resp) ? resp : resp?.data || [];
        setHangupCauseMap(
          Object.fromEntries(
            list.map((item: any) => [item.hangup_cause_code, item.message || item.hangup_cause_reason || '']),
          ),
        );
      })
      .catch(() => setHangupCauseMap({}));
  }, [detailCall, hangupCauseMap]);
  const detailRow = useMemo(
    () =>
      detailCall && {
        ...detailCall,
        call_type: isIncomingCall(detailCall) ? 'incoming' : 'outgoing',
        start_time: detailCall.start_time || detailCall.time_of_call,
      },
    [detailCall],
  );

  const columns = useMemo(
    () => [
      ...(showAgent ? [textCol('agent_name', 'agent_name', 'Agent')] : []),
      {
        id: 'direction',
        accessorFn: (call) =>
          [isIncomingCall(call) ? 'Incoming' : 'Outgoing', call.agent_profile_type].filter(Boolean).join(', '),
        header: 'Call',
        cell: ({ row }) => {
          const call = row.original;
          const isIncoming = isIncomingCall(call);
          return (
            <span className="inline-flex flex-wrap items-center gap-1">
              <Pill tone={isIncoming ? 'primary' : 'good'}>
                {isIncoming ? <FiPhoneIncoming size={12} /> : <FiPhoneOutgoing size={12} />}
                {isIncoming ? 'Incoming' : 'Outgoing'}
              </Pill>
              {call.agent_profile_type ? <Pill tone="primary">{call.agent_profile_type}</Pill> : null}
            </span>
          );
        },
        enableSorting: true,
      },
      {
        id: 'call_type',
        accessorFn: (call) => callTypeTags(call).join(', '),
        header: 'Call Type',
        cell: ({ row }) => {
          const tags = callTypeTags(row.original);
          return tags.length ? (
            <span className="inline-flex flex-wrap items-center gap-1">
              {tags.map((tag) => (
                <Pill key={tag}>{tag}</Pill>
              ))}
            </span>
          ) : (
            '-'
          );
        },
        enableSorting: true,
      },
      {
        id: 'number',
        accessorKey: 'number',
        header: 'Customer Number',
        enableSorting: true,
        cell: ({ row }) => {
          const call = row.original;
          // dial_number is only sent for the agent's own callable rows; the click asks the
          // backend again (by call id) so break / call / disposition / login are DB-checked.
          if (!onCallNumber || showAgent || !call?.id || !call?.dial_number) return call?.number || '-';
          return (
            <span className="inline-flex items-center gap-1.5">
              {call.number || '-'}
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-6 w-6"
                disabled={Boolean(callBlockedReason)}
                title={callBlockedReason || 'Call this number'}
                aria-label="Call this number"
                onClick={(e) => {
                  e.stopPropagation();
                  onCallNumber(call);
                }}
              >
                <Phone size={12} />
              </Button>
            </span>
          );
        },
      },
      { id: 'caller_profile', accessorKey: 'caller_profile', header: 'Contact', enableSorting: true },
      {
        id: 'status',
        accessorFn: (call) => [callStatusLabel(call), agentOutcome(call)].filter(Boolean).join(' / '),
        header: 'Call Status',
        cell: ({ row }) => {
          const call = row.original;
          const status = callStatusLabel(call);
          const outcome = agentOutcome(call);
          return (
            <span className="inline-flex flex-col items-start gap-0.5">
              <StatusPill label={status} />
              {outcome && outcome !== status ? <StatusPill label={outcome} /> : null}
            </span>
          );
        },
        enableSorting: true,
      },
      // Agent leg status: outbound ~always Connected, inbound failure = Missed/Declined sub-label.
      // Hidden as redundant; calls.call_status still stored.
      // {
      //   id: 'agent_call_status',
      //   accessorFn: (call) => CALL_STATUS[call.call_status]?.[0] || '',
      //   header: 'Agent Status',
      //   cell: ({ getValue }) => {
      //     const label = getValue();
      //     return label ? <StatusPill label={label} /> : '-';
      //   },
      //   enableSorting: true,
      // },
      {
        id: 'duration',
        accessorFn: talkSeconds,
        header: 'Call Duration',
        cell: ({ getValue }) => formatDuration(getValue()),
        enableSorting: true,
      },
      {
        id: 'time',
        accessorKey: 'time_of_call',
        header: 'Time',
        cell: ({ row }) => {
          const t = row.original.time_of_call || row.original.start_time;
          return t ? moment(t).format('DD-MM-YYYY HH:mm:ss') : '-';
        },
        enableSorting: true,
      },
      {
        id: 'bridge_ring_duration',
        accessorFn: (call) => Number(call.bridge_ring_duration) || 0,
        header: 'Agent Ring',
        cell: ({ getValue }) => formatDuration(getValue()),
        enableSorting: true,
      },
      // durationCol('other_party_ring_duration', 'other_party_ring_duration', 'Other Party Ring'),
      // durationCol('patch_duration', 'patch_duration', 'Talk Time'),
      durationCol('hold_duration', 'hold_duration', 'Hold'),
      durationCol('mute_duration', 'mute_duration', 'Mute'),
      durationCol('wrap_up_seconds', 'wrap_up_seconds', 'Wrap-up'),
      textCol('did', 'did', 'DID'),
      textCol('campaign_name', 'campaign_name', 'Campaign', 'No campaign'),
      textCol('call_group_name', 'call_group_name', 'Call Group', 'No call group'),
      mediaCol('recording', 'Recording'),
      mediaCol('voicemail', 'Voicemail'),
      {
        id: 'transcript',
        accessorFn: (call) => transcriptLineCount(call?.trace_id),
        header: 'Transcript',
        enableSorting: true,
        size: 90,
        cell: ({ row }) => {
          const lineCount = transcriptLineCount(row.original?.trace_id);
          if (!lineCount) return <span className="text-muted-foreground">-</span>;
          return (
            <CallTranscriptButton
              traceId={row.original?.trace_id}
              lineCount={lineCount}
              className="h-7 w-7"
            />
          );
        },
      },
      idCol('call_sid', 'call_sid', 'Call ID'),
      idCol('trace_id', 'trace_id', 'Trace ID'),
    ].map((column) => ({
      ...column,
      enableGlobalFilter: SEARCHABLE_COLUMN_IDS.has(column.id),
    })),
    [transcriptLineCount, showAgent, onCallNumber, callBlockedReason]
  );
  const searchPlaceholder = showAgent ? 'Search agent, call ID, trace ID, DID...' : 'Search call ID, trace ID, DID...';
  const visibleColumns = columns.filter(
    (c) =>
      (c.id !== 'transcript' || canViewTranscripts) &&
      (c.id !== 'recording' || canViewRecordings) &&
      (c.id !== 'voicemail' || canViewVoicemails),
  );

  const exportCallLog = async (table) => {
    if (exportingRef.current) return;
    exportingRef.current = true;
    try {
      await exportCalls(table);
    } finally {
      exportingRef.current = false;
    }
  };

  const exportCalls = async (table) => {
    let calls = table.getPrePaginationRowModel().rows.map(({ original }) => original);
    if (fetchExportRows) {
      const toastId = toast.loading('Preparing export…');
      try {
        const result = await fetchExportRows();
        // Same Missed / Declined blanking as the on-screen agent's own log.
        calls = showAgent ? result.rows : result.rows.map((r) => ({ ...r, is_missed: false, is_declined: false }));
        // Off-page rows have no transcript lookup yet.
        if (canViewTranscripts) await loadTranscriptAvailability(calls.map((c) => c.trace_id));
        toast.dismiss(toastId);
        if (result.total > result.fetched) {
          toast.warning(`Exported the latest ${result.fetched} of ${result.total} calls. Narrow the date range to export the rest.`);
        }
      } catch {
        toast.dismiss(toastId);
        toast.error('Could not load the calls to export');
        return;
      }
    }
    let dispositions = null;
    try {
      dispositions = await postApiCall(API_DISPOSITIONS_BY_CALLS, {
        calls: calls.map((c) => ({ call_sid: c.call_sid || null, trace_id: c.trace_id || null })),
      });
    } catch {
      toast.error('Could not load dispositions; exporting without them');
    }
    const dispoOf = (i) => (Array.isArray(dispositions) ? dispositions[i] : null);
    const dispoDetails = (d) =>
      Object.entries(d?.data && typeof d.data === 'object' ? d.data : {})
        .map(([key, value]) => `${fmtFieldLabel(key)}: ${fmtFieldValue(value)}`)
        .join('; ') || '-';
    const dispoSubmitted = (d) =>
      [at(d.created_at), d.edited_at ? `edited ${at(d.edited_at)}` : '', d.is_auto_submit ? 'auto-submitted' : '']
        .filter(Boolean)
        .join(', ');
    const mediaUrls = (call, kind) =>
      (call?.media || [])
        .filter((m) => m?.kind === kind)
        .map((m) => mediaUrl(m) || 'Not ready yet')
        .join(' | ') || '-';
    const fields = [
      ['Time', (c) => at(c.time_of_call || c.start_time)],
      ...(showAgent ? [['Agent', (c) => c.agent_name || '-']] : []),
      ['Call', (c) => [isIncomingCall(c) ? 'Incoming' : 'Outgoing', c.agent_profile_type].filter(Boolean).join(', ')],
      ['Call Type', (c) => callTypeTags(c).join(', ')],
      ['Customer Number', (c) => c.number || '-'],
      ['Contact', (c) => c.caller_profile || '-'],
      ['Call Status', (c) => [callStatusLabel(c), agentOutcome(c)].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(' / ')],
      ['Agent Call Status', (c) => [CALL_STATUS[c.call_status]?.[0], agentOutcome(c)].filter(Boolean).join(' / ') || '-'],
      ['Agent Ring', (c) => secs(c.bridge_ring_duration)],
      ['Customer Call Status', (c) => CALL_STATUS[c.other_party_call_status]?.[0] || '-'],
      ['Customer Ring', (c) => secs(c.other_party_ring_duration)],
      ['Hangup Cause', (c) => capitalize(c.hangup_cause) || '-'],
      ['Call Duration', (c) => secs(talkSeconds(c))],
      ['Talk Time', (c) => secs(c.patch_duration)],
      ['Hold', (c) => secs(c.hold_duration)],
      ['Mute', (c) => secs(c.mute_duration)],
      ['Wrap-up', (c) => secs(c.wrap_up_seconds)],
      ['DID', (c) => c.did || '-'],
      ['Campaign', (c) => c.campaign_name || 'No campaign'],
      ['Call Group', (c) => c.call_group_name || 'No call group'],
      ...(canViewRecordings ? [['Recording', (c) => mediaUrls(c, 'recording')]] : []),
      ...(canViewVoicemails ? [['Voicemail', (c) => mediaUrls(c, 'voicemail')]] : []),
      ...(canViewTranscripts ? [['Transcript', (c) => (transcriptLineCount(c.trace_id) ? 'Yes' : 'No')]] : []),
      ['Call ID', (c) => c.call_sid || '-'],
      ['Trace ID', (c) => c.trace_id || '-'],
      ...(Array.isArray(dispositions)
        ? [
            ['Disposition Form', (c, i) => dispoOf(i)?.disposition_form_name || (dispoOf(i) ? '-' : 'No disposition')],
            ['Disposition Submitted', (c, i) => (dispoOf(i) ? dispoSubmitted(dispoOf(i)) : '-')],
            ['Disposition Details', (c, i) => (dispoOf(i) ? dispoDetails(dispoOf(i)) : '-')],
          ]
        : []),
    ];
    downloadCsv(
      [fields.map(([label]) => label), ...calls.map((call, i) => fields.map(([, get]) => get(call, i)))],
      stampedFileName(exportName),
    );
  };

  return (
    <>
      <DataTable
        tableId={tableId}
        initialColumnVisibility={HIDDEN_BY_DEFAULT}
        renderSubRow={(call) => (
          <CallDetailRow call={call} onView={setDetailCall} />
        )}
        getRowId={(call, idx) => String(call?.id ?? call?.call_sid ?? idx)}
        columns={visibleColumns}
        data={rows}
        isLoading={loading}
        pageSize={10}
        emptyTitle="No calls"
        emptyDescription="No calls found for the selected date range"
        onExport={exportCallLog}
        exportLabel={exportLabel}
        searchPlaceholder={searchPlaceholder}
        {...(serverPaging ? { serverPagination: serverPaging } : {})}
        {...(onSearchChange
          ? {
              leftContent: (
                <div className="relative w-full max-w-sm">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder={searchPlaceholder}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="!pl-11"
                    style={{ paddingLeft: '2.75rem' }}
                  />
                </div>
              ),
            }
          : {})}
      />
      <CdrCallDetailDialog
        row={detailRow}
        did={detailRow?.did || '-'}
        pageRows={rows || []}
        userRole={getUser()?.role}
        hangupCauseMap={hangupCauseMap || {}}
        transcriptLines={detailRow ? transcriptLineCount(detailRow.trace_id) : 0}
        onClose={() => setDetailCall(null)}
      />
    </>
  );
}
